import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, AlertTriangle, CheckCircle, Clock, XCircle, Package, Filter } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetOrders, adminGetOrderPipeline } from '../../services/api';

// Status config
const STATUS_CFG = {
  new:                  { label: 'New',                color: 'text-zinc-400 bg-zinc-800 border-zinc-700', dot: 'bg-zinc-500' },
  payment_pending:      { label: 'Payment Pending',    color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', dot: 'bg-yellow-500' },
  payment_verified:     { label: 'Payment Verified',   color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', dot: 'bg-blue-500' },
  validation_pending:   { label: 'Needs Validation',   color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', dot: 'bg-purple-500' },
  validation_hold:      { label: 'Validation Hold',    color: 'text-red-400 bg-red-500/10 border-red-500/20', dot: 'bg-red-500' },
  ready_for_production: { label: 'Ready for Production', color: 'text-green-400 bg-green-500/10 border-green-500/20', dot: 'bg-green-500' },
  batched:              { label: 'Cut Sheet Queue',       color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20', dot: 'bg-cyan-500' },
  in_production:        { label: 'In Production',       color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', dot: 'bg-blue-400' },
  serialized:           { label: 'Serialized',          color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', dot: 'bg-purple-400' },
  packed:               { label: 'Packed',              color: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20', dot: 'bg-indigo-500' },
  ready_to_ship:        { label: 'Ready to Ship',       color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', dot: 'bg-orange-500' },
  on_hold:              { label: 'On Hold',             color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', dot: 'bg-orange-400' },
  shipped:              { label: 'Shipped',             color: 'text-teal-400 bg-teal-500/10 border-teal-500/20', dot: 'bg-teal-500' },
  cancelled:            { label: 'Cancelled',           color: 'text-zinc-600 bg-zinc-900 border-zinc-800', dot: 'bg-zinc-700' },
};

const PIPELINE_STAGES = [
  { key: 'new', icon: Package },
  { key: 'payment_pending', icon: Clock },
  { key: 'payment_verified', icon: CheckCircle },
  { key: 'validation_pending', icon: AlertTriangle },
  { key: 'validation_hold', icon: XCircle },
  { key: 'ready_for_production', icon: CheckCircle },
  { key: 'batched', icon: Package },
  { key: 'in_production', icon: CheckCircle },
  { key: 'serialized', icon: CheckCircle },
  { key: 'packed', icon: Package },
  { key: 'ready_to_ship', icon: CheckCircle },
  { key: 'on_hold', icon: AlertTriangle },
  { key: 'shipped', icon: CheckCircle },
];

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.new;
  return (
    <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// Navigate to the admin page where this order currently lives in the pipeline
// Passes ?highlight=_id so the target page can scroll to + ring the exact card
function getOrderRoute(order) {
  const { status, _id } = order;
  if (['ready_for_production', 'batched', 'in_production'].includes(status)) {
    return `/admin/production?highlight=${_id}`;
  }
  if (status === 'serialized') return `/admin/serialization?highlight=${_id}`;
  if (['ready_to_ship', 'shipped'].includes(status)) return `/admin/shipping?highlight=${_id}`;
  // All other states → order detail
  return `/admin/orders/${_id}`;
}

function ValidationBadge({ validation }) {
  if (!validation?.checked_at) {
    return <span className="font-inter text-[10px] text-zinc-600 uppercase tracking-wider">Not run</span>;
  }
  if (validation.error_count > 0) {
    return (
      <span className="flex items-center gap-1 font-inter text-[10px] font-bold text-red-400">
        <XCircle size={10} />{validation.error_count} error{validation.error_count !== 1 ? 's' : ''}
        {validation.warning_count > 0 && `, ${validation.warning_count} warn`}
      </span>
    );
  }
  if (validation.warning_count > 0) {
    return (
      <span className="flex items-center gap-1 font-inter text-[10px] font-bold text-yellow-400">
        <AlertTriangle size={10} />{validation.warning_count} warning{validation.warning_count !== 1 ? 's' : ''}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 font-inter text-[10px] font-bold text-green-400">
      <CheckCircle size={10} />Valid
    </span>
  );
}

export default function AdminOrders() {
  const { token } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const load = (status, type) => {
    setLoading(true);
    Promise.all([
      adminGetOrders(token, status === 'all' ? null : status, type === 'all' ? null : type),
      adminGetOrderPipeline(token),
    ]).then(([o, p]) => { setOrders(o); setPipeline(p); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(statusFilter, typeFilter); }, [token, statusFilter, typeFilter]);

  const needsAttention = (pipeline?.counts?.validation_hold || 0) + (pipeline?.counts?.on_hold || 0);

  return (
    <div data-testid="admin-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">ORDER PIPELINE</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">
            {pipeline?.total || 0} total orders
            {needsAttention > 0 && <span className="ml-2 text-orange-400 font-semibold">· {needsAttention} need attention</span>}
          </p>
        </div>
      </div>

      {/* Pipeline stats */}
      {pipeline && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6">
          {PIPELINE_STAGES.map(({ key, icon: Icon }) => {
            const cfg = STATUS_CFG[key];
            const count = pipeline.counts?.[key] || 0;
            return (
              <button
                key={key}
                data-testid={`pipeline-${key}`}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
                className={`bg-[#1a1a1a] border p-3 text-center transition-all hover:border-white/30 ${statusFilter === key ? 'border-orange-500' : 'border-white/10'}`}
              >
                <div className={`font-bebas text-2xl tracking-wider ${count > 0 ? cfg.color.split(' ')[0] : 'text-zinc-700'}`}>{count}</div>
                <div className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider mt-0.5 leading-tight">{cfg.label}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex gap-2 flex-wrap">
          <span className="font-inter text-xs text-zinc-600 uppercase tracking-wider self-center">Status:</span>
          {['all', 'new', 'payment_pending', 'payment_verified', 'validation_pending', 'validation_hold', 'ready_for_production', 'batched', 'in_production', 'serialized', 'packed', 'ready_to_ship', 'on_hold', 'shipped', 'cancelled'].map(s => (
            <button
              key={s}
              data-testid={`filter-status-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`font-inter text-[10px] px-2.5 py-1.5 border transition-colors uppercase tracking-wider ${
                statusFilter === s ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_CFG[s]?.label || s}
            </button>
          ))}
        </div>
        <div className="flex gap-2 ml-auto">
          <span className="font-inter text-xs text-zinc-600 uppercase tracking-wider self-center">Type:</span>
          {['all', 'dealer', 'public'].map(t => (
            <button
              key={t}
              data-testid={`filter-type-${t}`}
              onClick={() => setTypeFilter(t)}
              className={`font-inter text-[10px] px-3 py-1.5 border transition-colors uppercase tracking-wider ${
                typeFilter === t ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Orders table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
          <table className="w-full" data-testid="orders-table">
            <thead>
              <tr className="border-b border-white/10">
                {['Order', 'Type', 'Customer / Business', 'Items', 'Total', 'Status', 'Validation', 'Date', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-inter text-xs text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {orders.map(o => {
                const cfg = STATUS_CFG[o.status] || STATUS_CFG.new;
                const needsAttn = ['validation_hold', 'on_hold'].includes(o.status);
                return (
                  <tr
                    key={o._id}
                    data-testid={`order-row-${o._id}`}
                    className={`hover:bg-white/3 transition-colors ${needsAttn ? 'bg-orange-500/3' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {needsAttn && <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot} animate-pulse`} />}
                        <div>
                          <Link
                            to={getOrderRoute(o)}
                            data-testid={`order-number-link-${o._id}`}
                            title={`Go to ${o.order_number} in pipeline`}
                            className="font-inter text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors hover:underline underline-offset-2"
                          >
                            {o.order_number}
                          </Link>
                          {o.has_nfa_items && <p className="font-inter text-[10px] text-orange-400">NFA</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${
                        o.type === 'dealer' ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' : 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                      }`}>{o.type || 'public'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-inter text-sm text-white">{o.business_name || o.customer_info?.name || '—'}</p>
                      <p className="font-inter text-xs text-zinc-600">{o.customer_info?.email || ''}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-inter text-sm text-zinc-400">{o.items?.length || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-bebas text-base text-white tracking-wide">${o.subtotal?.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-4 py-3"><ValidationBadge validation={o.validation} /></td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-xs text-zinc-500">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/orders/${o._id}`}
                        data-testid={`view-order-${o._id}`}
                        className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-white transition-colors whitespace-nowrap"
                      >
                        Review <ChevronRight size={12} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {orders.length === 0 && (
            <div className="text-center py-16">
              <Package size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO ORDERS FOUND</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">Orders will appear here as dealers place them</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
